/**
 * Appends the live price to the main add-to-cart button, matching the
 * "Add to cart – $79.98 $119.97" pattern used across this category.
 *
 * The figure comes from the selected bundle tier, not from the theme's price
 * node. Two reasons: the price node was removed from the buy box (the tiers
 * carry the price now), and it only ever showed the variant's one-time price —
 * so with "Subscribe & save" active the button read $39.99 while the selected
 * tier read $29.99. The tier is the only element that knows both the pack size
 * and the purchase mode.
 *
 * Implemented as an enhancement rather than an edit to Horizon's add-to-cart
 * block: the button label comes from a locale key with no per-block override,
 * and editing stock theme files creates conflicts on theme updates.
 */
(() => {
  const BUTTON = 'button.add-to-cart-button:not(.sticky-add-to-cart__button)';
  const DETAILS = '[class*="product-details"], .product-information';

  const textOf = (el) => (el?.textContent || '').replace(/\s+/g, ' ').trim();

  /** Selected tier and mode, or null when this template has no bundle selector. */
  const fromBundle = () => {
    const root = document.querySelector('.bundle-selector');
    if (!root) return null;

    const checked = root.querySelector('.bundle-tier__input:checked');
    const tier = checked ? checked.closest('.bundle-tier') : root.querySelector('.bundle-tier');
    if (!tier) return null;

    // Read the mode from <html>, not the selector root: Horizon re-renders the
    // root on every tier change with data-mode="sub", which would flash the
    // subscription price on the button while one-time is active.
    const isSub = document.documentElement.getAttribute('data-ms-bundle-mode') !== 'onetime';
    const current = isSub ? tier.dataset.subPrice : tier.dataset.onetimePrice;
    if (!current) return null;

    // Only a genuine discount gets a strikethrough. One bottle bought outright
    // is the full price, so compare and current match and nothing is shown.
    const compare = tier.dataset.comparePrice;
    return { current, compare: compare && compare !== current ? compare : '' };
  };

  /**
   * Fallback for templates without a bundle selector. Several `.price` nodes
   * exist; only the one inside the product details column tracks the variant.
   */
  const fromPriceNode = () => {
    const priceEl = [...document.querySelectorAll('.price')].find(
      (el) =>
        el.closest(DETAILS) &&
        !el.closest('.bundle-selector') &&
        !el.closest('.sticky-add-to-cart')
    );
    if (!priceEl) return null;

    const current =
      textOf(priceEl.querySelector('sale-price, .price__current')) ||
      textOf(priceEl).split(' ')[0];
    if (!current) return null;

    const compare = textOf(priceEl.querySelector('compare-at-price, .price__compare, s'));
    return { current, compare: compare && compare !== current ? compare : '' };
  };

  let observer = null;
  let writing = false;

  const decorate = () => {
    // decorate() mutates the button, which lives inside the observed subtree.
    // Without this guard the observer retriggers itself and freezes the page.
    if (writing) return;

    const button = document.querySelector(BUTTON);
    if (!button) return;

    const priced = fromBundle() || fromPriceNode();
    if (!priced) return;

    let label = button.querySelector('[data-ms-atc-label]');
    if (!label) {
      // Capture the original label exactly once so repeated runs never stack.
      const textNode = [...button.childNodes].find(
        (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
      );
      label = document.createElement('span');
      label.dataset.msAtcLabel = '';
      label.dataset.msAtcBase = (textNode ? textNode.textContent : button.textContent).trim();
      if (textNode) textNode.replaceWith(label);
      else button.appendChild(label);
    }

    const desired = `${label.dataset.msAtcBase} – ${priced.current}`;
    let strike = button.querySelector('[data-ms-atc-compare]');
    const haveCompare = strike ? strike.textContent : '';

    // Nothing to do: bail before touching the DOM at all.
    if (label.textContent === desired && haveCompare === priced.compare) return;

    writing = true;
    observer?.disconnect();

    label.textContent = desired;

    if (priced.compare) {
      if (!strike) {
        strike = document.createElement('s');
        strike.dataset.msAtcCompare = '';
        strike.className = 'ms-atc-compare';
        label.after(strike);
      }
      strike.textContent = priced.compare;
    } else if (strike) {
      strike.remove();
    }

    writing = false;
    observe();
  };

  // Horizon replaces the price node on variant change rather than mutating it,
  // so observe a stable ancestor and re-resolve on every mutation. The mode
  // toggle only flips an attribute on the selector root, which a childList
  // observer never sees — hence the explicit attribute filter and the
  // click/change listeners below.
  const observe = () => {
    const host = document.querySelector(DETAILS) || document.body;
    if (!observer) observer = new MutationObserver(() => decorate());
    observer.observe(host, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-mode', 'checked'],
    });
  };

  const start = () => {
    decorate();
    observe();
    document.addEventListener('change', (e) => {
      if (e.target.closest?.('.bundle-selector')) decorate();
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest?.('.bundle-selector')) requestAnimationFrame(decorate);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
